export function formatPromotionMoney(price, template) {
  const match = template.match(/[0-9][0-9\s,.'\u00a0\u202f]*[0-9]/);
  if (!match) return null;
  const decimal = match[0].match(/([.,])(\d{2})$/);
  const integerSample = decimal ? match[0].slice(0, -3) : match[0];
  const separator = integerSample.replace(/[0-9]/g, '')[0] || '';
  const parts = (price / 100).toFixed(decimal ? 2 : 0).split('.');
  const integer = separator ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator) : parts[0];
  return template.replace(match[0], integer + (decimal ? decimal[1] + parts[1] : ''));
}

export function quotePromotion(product, variant, snapshot, now = Date.now()) {
  if (!snapshot || snapshot.version !== 1 || snapshot.blocked || !Array.isArray(snapshot.rules) || snapshot.rules.length !== 1) return null;
  if (!(snapshot.expiresAt > now) || snapshot.expiresAt > now + 60000 || snapshot.generatedAt > now + 5000) return null;
  if (!variant?.available || variant.unitPrice || !Number.isSafeInteger(variant.price) || variant.price <= 0) return null;
  const applicable = snapshot.rules.filter(rule => {
    if (!(rule.fraction > 0 && rule.fraction < 1) || Date.parse(rule.startsAt) > now || !Number.isFinite(Date.parse(rule.startsAt))) return false;
    if (rule.endsAt && (!Number.isFinite(Date.parse(rule.endsAt)) || Date.parse(rule.endsAt) <= now)) return false;
    return rule.all === true || rule.products?.includes(product.id) || rule.variants?.includes(variant.id) || rule.collections?.some(id => product.collections.includes(id));
  });
  if (!applicable.length) return null;
  const fraction = Math.max(...applicable.map(rule => rule.fraction));
  const price = Math.round(variant.price * (1 - fraction));
  return price < variant.price ? { price, original: variant.price, fraction } : null;
}

if (typeof window !== 'undefined' && !customElements.get('madlac-promotion')) {
  const configElement = document.getElementById('madlac-promotions-config');
  const config = configElement ? JSON.parse(configElement.textContent) : {};
  const local = ['localhost', '127.0.0.1'].includes(location.hostname);
  const previewValue = Number(new URLSearchParams(location.search).get('madlac_promo_preview'));
  const preview = local && [20, 30].includes(previewValue);
  const endpoint = local ? 'http://127.0.0.1:8090/rules' : config.endpoint;
  const enabled = local || config.enabled === true;
  let snapshot = null;
  let pending = false;
  let expiration = null;
  const instances = new Set();

  const refreshElements = () => instances.forEach(element => element.update());

  class PromotionPrice extends HTMLElement {
    connectedCallback() {
      try { this.product = JSON.parse(this.querySelector('script').textContent); } catch { return; }
      this.output = this.querySelector('.madlac-promotion-offer');
      this.displayText = '';
      instances.add(this);
      this.update();
    }
    disconnectedCallback() { instances.delete(this); }
    static get observedAttributes() { return ['data-variant-id']; }
    attributeChangedCallback() { if (this.product) this.update(); }
    money(price) {
      const formatted = formatPromotionMoney(price, this.dataset.moneyTemplate || '');
      if (formatted) return formatted;
      return new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency', currency: this.dataset.currency,
        ...(this.dataset.currency === 'HUF' ? { maximumFractionDigits: 0, minimumFractionDigits: 0 } : {})
      }).format(price / 100);
    }
    update() {
      const variants = this.dataset.mode === 'card'
        ? this.product.variants.filter(variant => variant.available)
        : this.product.variants.filter(variant => variant.id === this.dataset.variantId);
      const values = variants.map(variant => ({ variant, quote: quotePromotion(this.product, variant, snapshot) }));
      const sorted = values.sort((a, b) => (a.quote?.price ?? a.variant.price) - (b.quote?.price ?? b.variant.price));
      const chosen = sorted[0];
      const result = chosen?.quote;
      const isRange = new Set(values.map(value => value.quote?.price ?? value.variant.price)).size > 1;
      const nextText = result ? this.money(result.price) : '';
      const signature = JSON.stringify([result, isRange, this.dataset.variantId]);
      if (this.signature === signature) return;
      this.signature = signature;
      this.displayText = nextText;
      this.parentElement.classList.toggle('madlac-promotion-active', Boolean(result));
      this.output.hidden = !result;
      this.output.replaceChildren();
      if (result) {
        const label = text => { const el = document.createElement('span'); el.className = 'visually-hidden'; el.textContent = text; return el; };
        const oldPrice = document.createElement('s');
        oldPrice.className = 'madlac-promotion-old';
        oldPrice.textContent = this.money(result.original);
        const newPrice = document.createElement('span');
        newPrice.className = 'madlac-promotion-new';
        newPrice.textContent = isRange ? this.dataset.fromLabel.replace('__PRICE__', nextText) : nextText;
        this.output.append(label(this.dataset.regularLabel), oldPrice, label(this.dataset.saleLabel), newPrice);
      }
      this.dispatchEvent(new CustomEvent('madlac:promotion-price', { bubbles: true }));
    }
  }
  customElements.define('madlac-promotion', PromotionPrice);

  async function refresh() {
    if (!enabled || pending || (!preview && !endpoint)) return;
    pending = true;
    try {
      if (preview) {
        const now = Date.now();
        snapshot = { version: 1, generatedAt: now, expiresAt: now + 30000, rules: [
          { id: 'local-preview', fraction: previewValue / 100, all: true, startsAt: new Date(now - 60000).toISOString(), endsAt: null }
        ] };
      } else {
        const url = new URL(endpoint, location.origin);
        if (!local && (url.protocol !== 'https:' || !['madlac.com', 'www.madlac.com', 'shopify-api.kicorki.pl'].includes(url.hostname))) throw Error('Invalid endpoint');
        const response = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: AbortSignal.timeout(8000) });
        if (!response.ok) throw Error('Unavailable');
        snapshot = await response.json();
      }
    } catch { snapshot = null; }
    finally {
      pending = false;
      clearTimeout(expiration);
      if (snapshot) expiration = setTimeout(() => { snapshot = null; refreshElements(); }, Math.max(0, Math.min(30000, snapshot.expiresAt - Date.now())));
      refreshElements();
    }
  }

  if (preview) {
    const notice = document.createElement('p');
    notice.className = 'madlac-promotion-preview';
    notice.textContent = `Podgląd promocji -${previewValue}%. Tylko wygląd - rabat w koszyku jest nieaktywny.`;
    document.body.prepend(notice);
  }
  if (enabled) {
    refresh();
    setInterval(() => { if (!document.hidden) refresh(); }, 10000);
    setInterval(refreshElements, 1000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) { snapshot = null; refreshElements(); refresh(); }
    });
    window.addEventListener('pageshow', () => { snapshot = null; refreshElements(); refresh(); });
  }
}
